/**
 * Brain build — REST shim replacing the legacy API Gateway WebSocket actions
 * ``startDeployment`` and ``syncNetwork``. Streaming progress now flows through
 * AppSync Events (see ``useAgentChannel``), so these endpoints are the plain
 * request side of that pipe.
 */
import { getRestApiBearerToken } from './apiToken';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

/**
 * Kick off a Brain Step Functions execution for the given network.
 *
 * The AppSync channel ``/default/agent/{scope}/{networkId}/default`` is the
 * live progress stream — callers should already be subscribed via
 * ``useAgentChannel`` before awaiting this call.
 *
 * @param {string} networkId
 * @param {string} prompt
 * @param {{ connectionId?: string, generateNetworkSkills?: boolean,
 *           changeType?: 'structural'|'prompt-refresh',
 *           forceRegeneratePrompts?: boolean, generative?: boolean }} [opts]
 * @returns {Promise<{ taskId: string, executionArn?: string, isUpdate?: boolean }>}
 */
export async function startBrainDeployment(networkId, prompt, opts = {}) {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(
    `${API_BASE_URL}/networks/${encodeURIComponent(networkId)}/start-deployment`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        prompt,
        ...(opts.connectionId ? { connectionId: opts.connectionId } : {}),
        // Architect skill kick-start (doc/arch/vertical/SKILLS.md). Default ON;
        // only send false to opt out (the backend treats absent as on).
        ...(opts.generateNetworkSkills === false ? { generateNetworkSkills: false } : {}),
        // Prompt-only refresh (NETWORK-TO-ROOMS.md §2.2): re-render agent prompts
        // from the existing plan with the latest rules — the brain SFN skips the
        // architect Opus re-plan. ``changeType:'prompt-refresh'`` implies
        // force-regenerate server-side; we send both explicitly for clarity.
        ...(opts.changeType ? { changeType: opts.changeType } : {}),
        ...(opts.forceRegeneratePrompts ? { forceRegeneratePrompts: true } : {}),
        // Generative build (GENERATIVE-NETWORKS.md §3a): route to Brain2 (manifest
        // architect) instead of the Simple brain. Default off — only sent when true.
        ...(opts.generative ? { generative: true } : {}),
        // THE NETWORK KIND (owner 2026-10-21): the Expert toggle — explicit, always sent from the Build deck
        // ('standard' | 'expert'); ``confirmStandard`` = the banner's yes to turning an expert team's network off.
        ...(opts.networkKind ? { networkKind: opts.networkKind } : {}),
        ...(opts.confirmStandard ? { confirmStandard: true } : {}),
      }),
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || data.message || `HTTP ${res.status}`);
  return data;
}

/**
 * Convenience: "Revise prompts only" — re-render every agent's prompt from the
 * network's EXISTING plan with the latest platform rules, WITHOUT the architect
 * Opus re-plan (fast + cheap). No new user prompt needed. See §2.2.
 * @param {string} networkId
 * @param {{ connectionId?: string }} [opts]
 */
export async function refreshNetworkPrompts(networkId, opts = {}) {
  return startBrainDeployment(networkId, '', {
    ...opts,
    changeType: 'prompt-refresh',
    forceRegeneratePrompts: true,
  });
}

/**
 * "Revise playbook only" (PROJECT networks, PROJECT-NETWORKS.md §4d) — re-run ONLY the
 * per-milestone playbook factory against the EXISTING org (skips the architect, approval,
 * and prompt/skill stages). The optional ``prompt`` steers every milestone's re-groom;
 * blank re-grooms from the org as-is. Applies immediately (no approval), like prompt-refresh.
 * A MINOR version bump. The backend falls back to a structural build for a non-project network.
 * @param {string} networkId
 * @param {string} [prompt] optional grooming guidance
 * @param {{ connectionId?: string }} [opts]
 */
export async function refreshNetworkTasks(networkId, prompt = '', opts = {}) {
  return startBrainDeployment(networkId, String(prompt || ''), {
    ...opts,
    changeType: 'task-refresh',
  });
}

/**
 * Rehydrate the latest task state for a network (replaces the legacy
 * WebSocket ``syncNetwork`` action). When the latest task is
 * ``AWAITING_APPROVAL`` the response includes the resolved design payload
 * and task token so the BuildTab can re-enter the approval modal.
 *
 * @param {string} networkId
 * @returns {Promise<{ status: string|null, taskId?: string, payload?: { taskToken: string, design?: string, prompt?: string, options?: string[] } }>}
 */
export async function fetchNetworkSyncState(networkId) {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(
    `${API_BASE_URL}/networks/${encodeURIComponent(networkId)}/sync-state`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || data.message || `HTTP ${res.status}`);
  return data;
}
